import React, { createContext, useContext, useReducer } from 'react';
import { Dispatcher } from './appActions';
import { appReducer } from './appReducer';
import messages from '../localizations/messages';
import { MessageProvider } from 'react-message-context';
import { MenuItem } from '../types/wordpress';
import { LOAD_STATES } from './common';

export enum Language {
  FI = 'fi',
  EN = 'en',
}

export interface CarouselWindowDimensions {
  height: number;
  width: number;
}

export interface CarouselItem {
  id: number;
  title: string;
  text: string;
  index: number;
  image: string;
}

export interface CarouselContent {
  content?: CarouselItem[];
  hideCarousel?: () => void;
  navigateToNext?: (event: React.MouseEvent<HTMLInputElement>) => void;
  navigateToPrev?: (event: React.MouseEvent<HTMLInputElement>) => void;
  dimensions?: CarouselWindowDimensions;
}

export interface Menu {
  loadState?: LOAD_STATES | false;
  items: MenuItem[];
}

export enum NotificationType {
  SUCCESS = 'success',
  ERROR = 'error',
  INFO = 'info',
}

export interface Notification {
  message: string;
  type: NotificationType;
}

export interface SystemNotification {
  translationKey: string;
}

export interface AppState {
  carousel?: CarouselContent;
  language: Language;
  menu: Menu;
  notifications: Notification[];
  systemNotification?: SystemNotification;
}

export const initialState: AppState = {
  notifications: [],
  menu: { loadState: false, items: [] },
  language: Language.FI,
};

const AppStateContext = createContext({} as AppState);
const AppDispatchContext = createContext((() => {}) as Dispatcher);

function AppStateProvider({ children }: { children: React.ReactNode }) {
  const [state, dispatch] = useReducer(appReducer, initialState);

  return (
    <AppStateContext.Provider value={state}>
      <MessageProvider locale={state.language} messages={(messages as any)[state.language]}>
        <AppDispatchContext.Provider value={dispatch}>{children}</AppDispatchContext.Provider>
      </MessageProvider>
    </AppStateContext.Provider>
  );
}

function useAppState() {
  const context = useContext(AppStateContext);
  if (context === undefined) {
    throw new Error('useAppState must be used within a AppStateProvider');
  }
  return context;
}

function useAppDispatch() {
  const context = useContext(AppDispatchContext);
  if (context === undefined) {
    throw new Error('useAppDispatch must be used within a AppStateProvider');
  }
  return context;
}

function useApp(): [AppState, Dispatcher] {
  return [useAppState(), useAppDispatch()];
}

export { AppStateProvider, useAppState, useAppDispatch, useApp };
